// 与 src/haruhiloop_cli/action_flavor_zh.py 1:1 对齐。
// 用 cyrb53 + mulberry32 替代 Python sha256 + random.Random，
// 行为等价（同输入同输出），不要求与 Python 二进制一致。

import { GameState } from "./models";
import { cyrb53, mulberry32 } from "../infrastructure/prng";

export const SCENE_CHOICE_FLAVORS: Record<string, readonly string[]> = {
  "clubroom.group_briefing": [
    "春日把白板拍得很响：今天谁迟到谁负责搬道具。",
    "古泉提议先梳理流程，朝比奈赶紧把便签颜色分好。",
    "你把会议压到十五分钟，活动室终于没再吵起来。",
  ],
  "clubroom.surprise_pitch": [
    "春日盯着草案越看越兴奋：这次一定要比上一轮更炸。",
    "你把惊喜节点写成三段式，古泉难得没唱反调。",
    "朝比奈念彩排词时卡壳，春日反而笑了出来。",
  ],
  "library.nagato_crosscheck": [
    "长门把样本对齐后只说了三个字：还是同一天。",
    "你报出时间戳，长门在页边写下新的偏移注记。",
    "图书馆的空调声太大，长门却准确听见了你的犹豫。",
  ],
  "home.homework_focus": [
    "你把作业摊满桌面，先从最短那栏下手。",
    "笔尖刮纸的声音持续了半小时，终于勾掉一块心结。",
    "最后一页写完时，你才发现天已经彻底黑了。",
  ],
};

export const FALLBACK_FLAVORS: readonly string[] = [
  "这一步没有显著波澜，但循环的纹路又被改写了一点。",
  "你按下了一个新的分岔，谁也不知道代价会落在哪个人身上。",
  "看似平常的安排，正在悄悄改变下一轮的起点。",
];

function seedFor(state: GameState, sceneId: string, choiceId: string, stepNumber: number): number {
  const count = state.scene_choice_counts[choiceId] ?? 0;
  const parts = [state.run_id, sceneId, choiceId, String(stepNumber), String(count)];
  if (state.random_seed !== null) parts.push(String(state.random_seed));
  return cyrb53(parts.join(" "));
}

export function pickChoiceFlavor(
  state: GameState,
  sceneId: string,
  choiceId: string,
  stepNumber: number,
): string | null {
  const key = `${sceneId}.${choiceId}`;
  const variants = SCENE_CHOICE_FLAVORS[key] ?? FALLBACK_FLAVORS;
  if (variants.length === 0) return null;

  const recent = state.scene_flavor_recent[key] ?? [];
  let banned: number | null = null;
  if (
    recent.length >= 2 &&
    recent[recent.length - 1] === recent[recent.length - 2]
  ) {
    banned = recent[recent.length - 1] ?? null;
  }
  const selectable = banned === null
    ? Array.from({ length: variants.length }, (_, i) => i)
    : Array.from({ length: variants.length }, (_, i) => i).filter((i) => i !== banned);
  if (selectable.length === 0) return variants[0] ?? null;

  const seed = seedFor(state, sceneId, choiceId, stepNumber);
  const rng = mulberry32(seed >>> 0);
  const idx = selectable[Math.floor(rng() * selectable.length)] ?? selectable[0]!;
  const last = recent[recent.length - 1];
  state.scene_flavor_recent[key] =
    recent.length >= 1 && last !== undefined ? [last, idx] : [idx];
  return variants[idx] ?? null;
}
